import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { T } from "@/lib/testIds";
import { CheckCircle2, Circle, ArrowRight, X, Sparkles } from "lucide-react";

/**
 * First-login checklist shown on the dashboard.
 * Hides when either all steps are done OR the workspace owner has dismissed it.
 */
export default function OnboardingChecklist() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/onboarding/status");
      setData(r.data);
    } catch (e) {
      // Non-fatal: dashboard still renders KPIs; log for visibility.
      // eslint-disable-next-line no-console
      console.warn("onboarding/status load failed", e);
    }
  };

  useEffect(() => { load(); }, []);

  const dismiss = async () => {
    setBusy(true);
    try {
      await api.post("/onboarding/dismiss");
      setData((d) => ({ ...d, dismissed: true }));
      toast.success("Setup checklist dismissed — you can revisit it any time");
    } catch {
      toast.error("Could not dismiss");
    } finally {
      setBusy(false);
    }
  };

  if (!data || data.dismissed || data.complete) return null;

  return (
    <div data-testid={T.onboardingChecklist} className="border co-hairline bg-white">
      <div className="flex items-start gap-4 p-5 lg:p-6 border-b co-hairline">
        <div className="w-10 h-10 bg-[#ECECFE] text-[#4338CA] grid place-items-center rounded-sm flex-shrink-0">
          <Sparkles className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="co-label">Set up your account · under 3 minutes</div>
          <h2 className="font-heading text-xl lg:text-2xl font-bold tracking-tight mt-1">
            Ready to start collecting?
          </h2>
          <p className="text-xs text-[#52525B] mt-1">
            Finish these three steps and CollectOne will chase every due payment for you.
          </p>
          <div className="mt-3 flex items-center gap-3">
            <div className="flex-1 h-1.5 bg-[#F4F4F5] overflow-hidden rounded-full">
              <div
                className="h-full bg-[#4338CA] transition-all duration-300"
                style={{ width: `${data.progress_pct}%` }}
              />
            </div>
            <span className="font-mono text-xs font-bold text-[#4338CA]">
              {data.progress_pct}%
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          disabled={busy}
          data-testid={T.onboardingDismiss}
          className="p-2 hover:bg-[#F4F4F5] rounded-sm text-[#52525B] disabled:opacity-50"
          title="Dismiss"
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>
      </div>
      <ol className="divide-y co-hairline">
        {data.steps.map((s, i) => (
          <li
            key={s.key}
            data-testid={`${T.onboardingStep}-${s.key}`}
            className={`flex items-center gap-4 p-4 lg:p-5 ${s.done ? "bg-[#F6FBF7]" : "bg-white"}`}
          >
            {s.done ? (
              <CheckCircle2 className="h-5 w-5 text-[#15803D] flex-shrink-0" strokeWidth={2} />
            ) : (
              <Circle className="h-5 w-5 text-[#A1A1AA] flex-shrink-0" strokeWidth={1.75} />
            )}
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-semibold ${s.done ? "text-[#52525B] line-through" : "text-[#0A0A0B]"}`}>
                Step {i + 1}. {s.title}
              </div>
            </div>
            {!s.done && (
              <Link
                to={s.cta_href}
                data-testid={`${T.onboardingCta}-${s.key}`}
                className="inline-flex items-center gap-1 text-xs font-semibold text-[#4338CA] hover:text-[#3730A3] whitespace-nowrap"
              >
                {s.cta_label} <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
              </Link>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
